import { useRef, useState } from "react";
import { toast } from "sonner";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "@/components/Dividers";
import { Sparkles, Building2, RefreshCw } from "lucide-react";
import { streamPitch } from "@/lib/pitchStream";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PitchOutput = ({ pitch, loading }) => {
  if (pitch) {
    return (
      <p className="text-lg md:text-xl leading-[1.75] text-white/90 whitespace-pre-wrap font-normal" data-testid="pitch-text">
        {pitch}
        {loading && <span className="inline-block w-3 h-6 ml-1 bg-[#FF1493] align-middle rec-dot" />}
      </p>
    );
  }
  return (
    <p className="font-tech text-xs tracking-[0.25em] uppercase text-white/30 flex items-center gap-3">
      <span className={`w-2 h-2 rounded-full ${loading ? "bg-[#FF1493] rec-dot" : "bg-white/20"}`} />
      {loading ? "Claude is drafting at cruising altitude..." : "Awaiting a company name — your pitch prints here."}
    </p>
  );
};

const PitchForm = ({ company, setCompany, loading, onSubmit }) => (
  <form onSubmit={onSubmit} className="flex flex-col md:flex-row gap-4" data-testid="pitch-form">
    <div className="relative flex-1">
      <Building2 size={16} strokeWidth={1.5} className="absolute left-5 top-1/2 -translate-y-1/2 text-[#FF1493]" />
      <input
        data-testid="pitch-company-input"
        value={company}
        onChange={(e) => setCompany(e.target.value)}
        placeholder="Your company — e.g. Medtronic, Google Health, Qantas..."
        maxLength={120}
        className="field-line w-full rounded-full border border-white/20 bg-black/40 pl-12 pr-6 py-4 text-base text-white placeholder:text-white/30"
      />
    </div>
    <button
      type="submit"
      disabled={loading || !company.trim()}
      data-testid="pitch-generate-button"
      className="inline-flex items-center justify-center gap-3 rounded-full bg-[#FF1493] text-black font-tech text-xs tracking-[0.25em] uppercase px-8 py-4 transition-transform duration-300 hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
    >
      {loading ? <RefreshCw size={14} className="animate-spin" /> : <Sparkles size={14} strokeWidth={1.5} />}
      {loading ? "Writing..." : "Generate pitch"}
    </button>
  </form>
);

export const Pitch = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const consoleY = useTransform(scrollYProgress, [0, 0.5, 1], [60, 0, -40]);
  const [company, setCompany] = useState("");
  const [pitch, setPitch] = useState("");
  const [loading, setLoading] = useState(false);

  const handleEvent = (ev) => {
    if (ev.t) setPitch((p) => p + ev.t);
    if (ev.error) toast.error(ev.error);
  };

  const generate = async (e) => {
    e.preventDefault();
    if (!company.trim() || loading) return;
    setLoading(true);
    setPitch("");
    try {
      await streamPitch(`${API}/pitch`, company.trim(), handleEvent);
    } catch (err) {
      console.error("Pitch generation failed", err);
      toast.error("Pitch generation hit turbulence. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section ref={ref} id="pitch" className="relative py-28 md:py-40 overflow-hidden" data-testid="pitch-section">
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <Reveal>
          <p className="font-tech text-[11px] tracking-[0.4em] uppercase text-[#FF66B2]">AI Gate Agent · Powered by Claude</p>
          <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tight mt-6">
            One click. <span className="italic text-[#FF1493]">Instant pitch.</span>
          </h2>
          <p className="text-white/60 text-base md:text-lg mt-6 max-w-xl leading-relaxed">
            Recruiter? Curious? Type your company and Claude writes a personalized "why hire me" pitch — stamped, scanned, and delivered live.
          </p>
        </Reveal>

        <motion.div style={{ y: consoleY }} className="will-change-transform">
          <Reveal delay={0.15}>
            <div className="mt-14 rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-md overflow-hidden" data-testid="pitch-console">
              <div className="flex items-center justify-between px-6 md:px-8 py-4 border-b border-white/10 font-tech text-[10px] tracking-[0.3em] uppercase text-white/40">
                <span className="flex items-center gap-2">
                  <Sparkles size={13} strokeWidth={1.5} className="text-[#FF1493]" /> Pitch Console · CLAUDE-SONNET-4.6
                </span>
                <span className="hidden md:inline">FLIGHT PATH: ME → YOU</span>
              </div>
              <div className="p-6 md:p-10">
                <PitchForm company={company} setCompany={setCompany} loading={loading} onSubmit={generate} />
                <div className="mt-8 min-h-[160px] rounded-2xl border border-dashed border-white/15 bg-black/40 p-6 md:p-8" data-testid="pitch-output">
                  <PitchOutput pitch={pitch} loading={loading} />
                </div>
              </div>
            </div>
          </Reveal>
        </motion.div>
      </div>
    </section>
  );
};
